import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
} from '@mui/material';
import { connect } from 'react-redux';

// custom utils
import { useWindowDimensions } from '../pages/Utils';
import { DrawChart } from '../charts/TVChart';
import stocks from '../data/stocks.json';

/**
 * A simple CSV parser. It assumes the CSV does not contain commas within the values.
 * For more complex CSVs, a more robust library like Papaparse would be better.
 * @param {string} csvText The CSV content as a string.
 * @returns {Array<Object>} An array of objects representing the rows.
 */
const parseCSV = (csvText) => {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) return []; // No data rows
  const headers = lines[0].split(',');
  const rows = lines.slice(1).map(line => {
    const values = line.split(',');
    return headers.reduce((object, header, index) => {
      object[header.trim()] = values[index] ? values[index].trim() : '';
      return object;
    }, {});
  });
  return rows;
};

const EarningsWatch = ({ navbarSize, topbarSize, interval, darkMode }) => {
  const [loading, setLoading] = useState(true);
  const [calendarData, setCalendarData] = useState([]);
  const [error, setError] = useState(null);
  const [symbol, setSymbol] = useState('');
  const [watchlist, setWatchlist] = useState([]);

const today = new Date();
const year = today.getFullYear();
const month = String(today.getMonth() + 1).padStart(2, '0');
const day = String(today.getDate()).padStart(2, '0');
const todayString = `${year}-${month}-${day}`;

// ...rest of your code...

  useEffect(() => {
    setLoading(true);

    // Check local storage for cached earnings data for today.
    const cachedDataRaw = localStorage.getItem('todays-earnings-data');
    if (cachedDataRaw) {
      try {
        const cachedData = JSON.parse(cachedDataRaw);
        if (cachedData.date === todayString) {
          setCalendarData(cachedData.data);
          const symbols = cachedData.data.map(d => d.symbol).filter(Boolean);
          setWatchlist(symbols);
          setSymbol(symbols.length > 0 ? symbols[0] : '');
          setError(null);
          setLoading(false);
          return; // Use cached data and skip fetch
        }
      } catch (error) {
        console.error("Failed to parse cached earnings data:", error);
        localStorage.removeItem('todays-earnings-data'); // Clear corrupted data
      }
    }

    // It's recommended to use your own API key from Alpha Vantage.
    // Using 'demo' key is subject to limitations.
    const apiKey = process.env.REACT_APP_ALPHA_VANTAGE_API_KEY || 'demo'; 
    // We fetch for 3 months to make sure we don't miss today's earnings due to timezone differences.
    const url = `https://www.alphavantage.co/query?function=EARNINGS_CALENDAR&horizon=3month&apikey=${apiKey}`;

    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then(csvText => {
        if (csvText.includes('Thank you for using Alpha Vantage!')) {
            setError('API call limit reached or invalid API key. Please try again later or use your own key.');
            setCalendarData([]);
            setLoading(false);
            return;
        }
        const allData = parseCSV(csvText);
        // console.log("Earnings data fetched:", allData.slice(0, 5));

        const stocksWithMinMarketCap = stocks.filter(stock => stock.MarketCap > 500000000);
        const validSymbols = new Set(stocksWithMinMarketCap.map(s => s.Ticker));
        const todaysEarnings = [];
        // Filter and order by the order of tickers in validSymbols
        stocksWithMinMarketCap.forEach(stock => {
          const found = allData.find(item =>
            item.reportDate === todayString && item.symbol === stock.Ticker
          );
          if (found) todaysEarnings.push(found);
        });

        // Cache the new data in local storage with today's date.
        localStorage.setItem('todays-earnings-data', JSON.stringify({ date: todayString, data: todaysEarnings }));

        setCalendarData(todaysEarnings);
        const symbols = todaysEarnings.map(d => d.symbol).filter(Boolean);
        setWatchlist(symbols);
        if (symbols.length > 0) {
            setSymbol(symbols[0]);
        } else {
            setSymbol('');
        }
        setError(null);
      })
      .catch(err => {
        console.error("Failed to fetch earnings calendar:", err);
        setError('Failed to fetch earnings data. Please check your connection or the API service.');
        setCalendarData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleSymbolSelect = (newSymbol) => {
    setSymbol(newSymbol);
  };

  const { height } = useWindowDimensions();
  const topHeight = navbarSize + 10;

    return (
    <Grid container sx={{ minHeight: height - topHeight, backgroundColor: "darkgray" }}>
        {/* Left panel takes remaining space */}
        <Grid item sx={{ flex: "1 1 auto", height: height - topHeight }}>
        {symbol ? (
            <DrawChart
            width="100%"
            height="100%"
            symbol={symbol}
            watchlist={watchlist}
            interval={interval}
            darkMode={darkMode}
            />
        ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Typography>{loading ? "Loading..." : "No earnings reported for today."}</Typography>
            </Box>
        )}
        </Grid>

    </Grid>
    );
};

const mapStateToProps = (state) => ({
  navbarSize: state.barSize.navbarSize,
  topbarSize: state.barSize.topbarSize,
  // Get chart settings from Redux state
  interval: state.settings.interval,
  darkMode: state.settings.darkMode,
});

export default connect(mapStateToProps)(EarningsWatch);
